'use client';

import type { ChangeEvent } from 'react';
import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogCloseButton,
} from '@wwe-portal/ui/components/dialog';
import { DollarIcon } from '@wwe-portal/ui/components/icons/dollar-icon';

interface ExchangeCalculatorProps {
  title: string;
  description: React.ReactNode;
  toCurrency: string;
  toCurrencyIcon: React.ReactNode;
  open: boolean;
  onClose: () => void;
  rate: number;
}
export const ExchangeCalculatorDialog = ({
  title,
  description,
  toCurrency,
  toCurrencyIcon,
  open,
  onClose,
  rate,
}: ExchangeCalculatorProps) => {
  const [fromValue, setFromValue] = useState<string>();
  const [toValue, setToValue] = useState<string>();
  const resetValue = () => {
    setFromValue('');
    setToValue('');
  };
  const handleValueEmpty = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      resetValue();
      return true;
    }
    return false;
  };
  const handleChangeFromValue = (e: ChangeEvent<HTMLInputElement>) => {
    if (handleValueEmpty(e)) return;
    const value = Number(e.target.value);
    if (Number.isNaN(value)) {
      return;
    }

    const nextToValue = Math.round(value * rate);
    setFromValue(e.target.value);
    setToValue(String(nextToValue));
  };

  const handleChangeToValue = (e: ChangeEvent<HTMLInputElement>) => {
    if (handleValueEmpty(e)) return;
    const value = Number(e.target.value);

    if (Number.isNaN(value)) {
      return;
    }

    const nextFromValue = Math.round(value / rate);
    setFromValue(String(nextFromValue));
    setToValue(String(value));
  };

  return (
    <Dialog
      onOpenChange={(value) => {
        if (!value) {
          onClose();
        }
      }}
      open={open}
    >
      <DialogContent
        className="bg-toolTipBgColor from-toolTipBgColor to-toolTipBgColor flex w-[calc(100%-64px)] max-w-[300px] flex-col gap-5 p-5"
        data-id="exchange-calculator-dialog"
        variant="dialog"
      >
        <div>
          <div className="text-text300 mb-4 text-sm font-semibold">{title}</div>
          <hr className="border-text800 mb-0" />
        </div>
        <div className="text-text600 grid grid-cols-2 gap-5 text-xs">
          <div className="relative flex flex-col gap-1">
            <span className="flex items-center gap-1">
              <DollarIcon className="inline size-3" /> USD
            </span>
            <input
              className="text-text600 bg-text800 focus:border-text600 my-1 block h-10 rounded-md border-solid p-[10px] focus:border-[2px] focus:outline-none"
              onChange={handleChangeFromValue}
              type="text"
              value={fromValue}
            />
            <div className="absolute -right-5 bottom-0 flex h-10 w-5 items-center justify-center">
              <span>=</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1">
              {toCurrencyIcon} {toCurrency}
            </span>

            <input
              className="text-text600 bg-text800 border-text800 focus:border-text600 my-1 block h-10 rounded-md border-solid p-[10px] focus:border-[2px] focus:outline-none"
              onChange={handleChangeToValue}
              type="text"
              value={toValue ?? ''}
            />
          </div>
        </div>
        <div className="text-text500 text-sm">{description}</div>
        <DialogCloseButton onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};
